import { GAME_MODE } from '~/interfaces';
import { Button } from '~/modules/elements/akui/button';
import { GameModeIcon } from '~/modules/elements/people-glyph';
import { RegisterFunc } from '~/modules/hooks/use-keyboard-nav';
import { SongQueueItem } from '~/modules/song-queue/song-queue';
import { QueueAction } from '~/routes/sing-a-song/song-selection/components/queue/queue-focus';

const MODE_LABELS: Record<string, string> = {
  [GAME_MODE.DUEL]: 'Duel',
  [GAME_MODE.PASS_THE_MIC]: 'Pass the mic',
  [GAME_MODE.CO_OP]: 'Co-op',
};

interface Props {
  item: SongQueueItem;
  position: number;
  // The card row's own register - the queue reads left to right, one stop per song.
  registerCard: RegisterFunc;
  // The action row's, which only the focused card fills.
  registerAction: RegisterFunc;
  // True while this card or one of its actions holds the focus - the actions show only then.
  open: boolean;
  // Which action the keyboard lands on when the row is entered: the one this position allows.
  firstAction: QueueAction;
  isFirst: boolean;
  isLast: boolean;
  onOpen: () => void;
  onMoveEarlier: () => void;
  onMoveLater: () => void;
  onRemove: () => void;
}

const actionClass = 'h-9! min-w-0! px-2! text-[0.9375rem]!';

export default function QueueCard({
  item,
  position,
  registerCard,
  registerAction,
  open,
  firstAction,
  isFirst,
  isLast,
  onOpen,
  onMoveEarlier,
  onMoveLater,
  onRemove,
}: Props) {
  const { song, override, addedBy } = item;
  const name = `queue-item-${position}`;

  const cardProps = registerCard(name, onOpen, 'Reorder or remove', position === 1, {
    control: { type: 'button', label: `${position} · ${song.title}`, icon: null },
  });

  return (
    <div
      className={`flex w-60 shrink-0 flex-col rounded-xl p-1.5 ${
        open ? 'bg-active/14 shadow-[inset_0_0_0_2px_rgb(255,165,0)]' : ''
      }`}>
      {/* The song block is what registers, not the card around it: the amber ring says "this is the
          card the actions belong to" and has to stay on while the keyboard is down in that row,
          which a registered element's own focus state cannot say. */}
      <div {...cardProps} data-song={song.id} data-open={open} className="flex min-w-0 cursor-pointer flex-col gap-1.5">
        <div
          className="relative aspect-video overflow-hidden rounded-lg bg-[#2b2b2b] bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('https://i3.ytimg.com/vi/${song.video}/hqdefault.jpg')` }}>
          <span className="absolute top-1.5 left-1.5 flex size-[1.625rem] items-center justify-center rounded-md bg-black/75 text-[0.9375rem] font-bold">
            {position}
          </span>
          {/* The override's mode, so a duet sitting between two duels is visible without reading. */}
          {override && (
            <span className="absolute bottom-1.5 left-1.5 flex h-6 items-center gap-1.5 rounded-md bg-black/75 px-2 text-[0.8125rem] font-bold uppercase">
              <GameModeIcon mode={override.mode} height={14} className="shrink-0" />
              {MODE_LABELS[override.mode] ?? override.mode}
            </span>
          )}
        </div>
        <span className="ph-no-capture truncate text-[1.0625rem] font-bold text-white">{song.title}</span>
        <span className="ph-no-capture truncate text-[0.9375rem] text-white/50">{song.artist}</span>
        {/* Which phone put it on - the room's way of seeing who is stacking the running order. */}
        {addedBy && <span className="truncate text-[0.9375rem] text-white/35">added by {addedBy}</span>}
      </div>

      {/* Reserved on every card, filled on the focused one: a row that appeared and disappeared with
          the focus would move every card in the lane as the selection walked along it. */}
      <div className="mt-auto flex h-[2.375rem] gap-1.5 pt-0.5">
        {open && (
          <>
            <Button
              size="mini"
              className={`${actionClass} flex-1`}
              data-test={`${name}-move-earlier`}
              aria-label="Move earlier in the queue"
              disabled={isFirst}
              {...registerAction(
                `${name}-move-earlier`,
                onMoveEarlier,
                'Move earlier',
                firstAction === 'move-earlier',
                {
                  disabled: isFirst,
                  control: { type: 'button', label: 'Move earlier', icon: null },
                },
              )}>
              ◀ move
            </Button>
            <Button
              size="mini"
              className={`${actionClass} flex-1`}
              data-test={`${name}-move-later`}
              aria-label="Move later in the queue"
              disabled={isLast}
              {...registerAction(`${name}-move-later`, onMoveLater, 'Move later', firstAction === 'move-later', {
                disabled: isLast,
                control: { type: 'button', label: 'Move later', icon: null },
              })}>
              move ▶
            </Button>
            <Button
              size="mini"
              className={`${actionClass} w-11`}
              data-test={`${name}-remove`}
              aria-label="Remove from queue"
              {...registerAction(`${name}-remove`, onRemove, 'Remove', firstAction === 'remove', {
                control: { type: 'button', label: 'Remove', icon: null },
              })}>
              ✕
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
